import React, { useMemo, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

import ExamReportList from '../../components/ExamReportList/ExamReportList';
import Header from '../../components/Header/Header';
import InputField from '../../components/InputField/InputField';
import ListPagination from '../../components/ListPagination/ListPagination';
import Loading from '../../components/Loading/Loading';
import LoadingSection from '../../components/Loading/LoadingSection';
import PageWithMenu from '../../components/PageWithMenu/PageWithMenu';
import SelectOptions from '../../components/SelectOptions/SelectOptions';
import { headerVariables } from '../../components/Header/HeaderStyle';
import EXAM_MODULES from '../../data/exam-modules';
import ActionButton from '../../components/ActionButton/ActionButton';

import SearchIcon from '../../assets/svgs/SearchIcon';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import EXAM_STATUS from '../../data/exam-status';
import EXAM_TYPES from '../../data/exam-types';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { useIsMounted } from '../../utils/useIsMounted';
import { breakpoints } from '../../ui/variables';

import { ExamsArchiveStyle } from './ExamsArchiveStyle';
// import { Redirect } from 'react-router';
// import { useNavigate } from 'react-router-dom';
const EXAMS_PER_PAGE = 30;

const ExamsArchive = () => {
  const breakpoint = useBreakpoint();
  const institution = useSelector((state) => state.settings.institution)
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);

  const [allExamsAmount, setAllExamsAmount] = useState(0);
  const [doctorsOptions, setDoctorsOptions] = useState([]);
  const [examOrderType, setExamOrderType] = useState('-date_created');
  const [examSummary, setExamSummary] = useState(null);
  const [exams, setExams] = useState([]);
  const [examsPage, setExamsPage] = useState(0);
  const [filterByDoctor, setFilterByDoctor] = useState(0);
  const [filterByExamType, setFilterByExamType] = useState(0);
  const [filterByPatient, setFilterByPatient] = useState(0);
  const [filterByStatus, setFilterByStatus] = useState(0);
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [searchExams, setSearchExams] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  
  // Ao trocar de instituição, resetar filtros/paginação e forçar reload
  useEffect(() => {
    if (!institution?.id) return;
    setLoading(true);
    // resetar estados locais relacionados a filtros/paginação
    setExamsPage(0);
    setFilterByDoctor(0);
    setFilterByExamType(0);
    setFilterByPatient(0);
    setFilterByStatus(0);
    setSearchExams('');
    setExamOrderType('-date_created');

    // limpar listas para mostrar loading/estado inicial
    setDoctorsOptions([]);
    setPatients([]);
    setExams([]);
    setAllExamsAmount(0);

    // limpar resumo para forçar mostrar LoadingSection enquanto o novo resumo é buscado
    setExamSummary(null);

    setApplyFilter(false);
  }, [institution?.id]);

  const getOptionList = useCallback(
    (options) => {
      const optionValues = Object.values(options);

      if (optionValues && optionValues.length) {
        const optionList = [];

        optionValues.forEach((option) => {
          if (option && option.key && option.key.length) {
            optionList.push({
              key: option.key,
              value: option.key,
              text: translate(option.key.toLocaleLowerCase(), language),
            });
          }
        });

        if (optionList.length) {
          return optionList;
        }
      }

      return [];
    },
    [language]
  );

  function corrigeModulos(exams) {
    const modulos = Object.keys(EXAM_MODULES);
  
    return exams.map(exam => {
      // Verifique se exam.module é nulo ou indefinido
      if (!exam.module) {
        exam.module = EXAM_MODULES.OUTROS.key;
      } else {
        const moduloEncontrado = modulos.find(mod => exam.module.includes(mod));
        if (moduloEncontrado) {
          exam.module = EXAM_MODULES[moduloEncontrado].key;
        } else {
          exam.module = EXAM_MODULES.OUTROS.key;
        }
      }
      return exam;
    });
  }
  

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }
      await Api.getInstitution().then((response) => {
        if(!response.data.count){
          window.location.href = '/no-institution';
        }
      });
      if (institution?.id) {
        // setLoading(true);
        const responses = await axios.all([
          Api.getDoctorUsers(),
          Api.getExamResumeByInstitution(institution?.id),
          Api.getPatientsWithExam(institution?.id),
        ]);

        let doctorUsersResponse = [];
        let examResumeByInstitutionResponse = null;
        let patientsWithExamResponse = [];

        if (responses?.length) {
          [
            doctorUsersResponse,
            examResumeByInstitutionResponse,
            patientsWithExamResponse,
          ] = responses;

          const currentDoctorOptions = [];
          let currentPatients = [];

          if (
            doctorUsersResponse?.status === 200 ||
            doctorUsersResponse?.data?.length
          ) {
            doctorUsersResponse?.data?.forEach((doctor) => {
              if (doctor?.id && doctor?.name?.length) {
                currentDoctorOptions.push({
                  key: doctor?.id,
                  value: doctor?.id,
                  text: doctor?.name,
                });
              }
            });
          }

          if (patientsWithExamResponse?.length) {
            currentPatients = patientsWithExamResponse;
          }

          setDoctorsOptions(currentDoctorOptions);
          setExamSummary(examResumeByInstitutionResponse);
          setPatients(currentPatients);
        }

        // setLoading(false);
      }
    })();
  }, [institution, isMounted]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      if (institution?.id) {
        setLoading(true);

        const currentParams = {
          institution: institution?.id,
          page: examsPage || 0,
        };
        let currentExams = [];
        let currentAllExamsAmount = 0;

        if (searchExams?.length) {
          currentParams.search = searchExams;
        }

        if (filterByStatus?.length) {
          currentParams.status = filterByStatus;
        }

        if (filterByExamType?.length) {
          currentParams.type = filterByExamType;
        }

        if (filterByDoctor) {
          currentParams.doctor = filterByDoctor;
        }

        if (filterByPatient) {
          currentParams.patient = filterByPatient;
        }

        if (examOrderType && examOrderType.length) {
          currentParams.order = examOrderType;
        }

        await Api.getExams(currentParams).then((response) => {
          if (response?.data?.result?.length) {
            currentExams = response?.data?.result;
            currentExams = corrigeModulos(currentExams);
          }

          if (response?.data?.count) {
            currentAllExamsAmount = response.data.count;
          }
        });
        setLoading(false);
        setExams(currentExams);
        setAllExamsAmount(currentAllExamsAmount);
        setApplyFilter(false);
      } 
    })();
  }, [
    applyFilter,
    examsPage,
    institution?.id,
    examOrderType,
    filterByDoctor,
    filterByExamType,
    filterByPatient, 
    filterByStatus,
    searchExams,
    isMounted,
  ]);

  const pageWithMenuStyle = useMemo(() => {
    if (headerVariables?.main?.height?.iphone) {
      return { paddingTop: `${headerVariables.main.height.iphone + 52}px` };
    }

    return null;
  }, []);

  const patientsOptions = useMemo(() => {
    const currentPatientsOptions = [];
    if (patients && patients.length) {
      patients.forEach((patient) => {
        if (patient && patient.id) {
          currentPatientsOptions.push({
            key: patient.id,
            value: patient.id,
            text:
              patient.name && patient.name.length
                ? patient.name
                : `${translate('patient', language)} ${patient.id}`,
          });
        }
      });
    }
    return currentPatientsOptions;
  }, [language, patients]);

  const handleShowMenu = () => setShowMenu(!showMenu);

  const handleResetFilters = useCallback(() => {
    setFilterByDoctor(0);
    setFilterByExamType(0);
    setFilterByPatient(0);
    setFilterByStatus(0);
    setSearchExams('');
    setExamOrderType('-date_created');
    setExamsPage(0);
    setApplyFilter(true);
  }, []);

  const onDeleteReportItem = useCallback(
    (id, study_id) => {
      (async () => {
        if (!isMounted()) {
          return;
        }

        setLoading(true);

        const response = await Api.deleteExamAndReport(id);

        if (response.status === 204) {
          (async () => {
            if (institution?.id) {
              const currentParams = {
                institution: institution?.id,
                page: examsPage,
              };
              let currentExams = [];
              let currentAllExamsAmount = 0;

              if (searchExams?.length) {
                currentParams.search = searchExams;
              }

              if (filterByStatus?.length) {
                currentParams.status = filterByStatus;
              }

              if (filterByExamType?.length) {
                currentParams.type = filterByExamType;
              }

              if (filterByDoctor) {
                currentParams.doctor = filterByDoctor;
              }

              if (filterByPatient) {
                currentParams.patient = filterByPatient;
              }

              if (examOrderType?.length) {
                currentParams.order = examOrderType;
              }

              const responseExams = await Api.getExams(currentParams);

              if (responseExams?.data?.result?.length) {
                currentExams = responseExams?.data?.result;
              }

              if (responseExams?.data?.count) {
                currentAllExamsAmount = responseExams?.data?.count;
              }
              setExams(currentExams);
              setAllExamsAmount(currentAllExamsAmount);
            }
          })();
        }

        setLoading(false);
      })();
    },
    [
      examOrderType,
      examsPage,
      filterByDoctor,
      filterByExamType,
      filterByPatient,
      filterByStatus,
      institution?.id,
      isMounted,
      searchExams,
    ]
  );

  const handleChangeSelect = (value, options, setValue) => {
    const currentOption = options.find(
      (option) => `${option.value}` === `${value}`
    );

    if (currentOption) {
      setExamsPage(0);
      setValue(currentOption.value);
    }
  };

  const examsPageAmount = useMemo(() => {
    let currentExamsPageAmount = 0;

    if (exams && exams.length && allExamsAmount) {
      currentExamsPageAmount = Math.ceil(allExamsAmount / EXAMS_PER_PAGE);
    }

    return currentExamsPageAmount;
  }, [allExamsAmount, exams]);

  const renderExamReportList = useMemo(() => {
    if (!loading) {
      return (
        <div>
          <ExamReportList
            examList={exams}
            onDeleteReportItem={onDeleteReportItem}
          />
          {examsPageAmount > 1 ? (
            <div className="exam-report-list-pagination-wrapper">
              <ListPagination
                page={examsPage}
                pageAmount={examsPageAmount}
                handleClickPagination={setExamsPage}
              />
            </div>
          ) : null}
        </div>
      );
    }

    return null;
  }, [exams, examsPage, examsPageAmount, loading, onDeleteReportItem]);

  const exportExamsToCSV = async () => {
    if (!institution?.id) return;

    try {
      const response = await Api.exportExamsToCSV(institution.id);
      
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, "0");
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const yyyy = today.getFullYear();
        const dateStr = `${dd}-${mm}-${yyyy}`;
        const fileName = `exames-${dateStr}.csv`;
        
        
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
    }
  };

/*   const exportExamsToCSV = async () => {
    if (!institution?.id) return;

    const headers = ["ID", "Nome do Paciente", "Status", "Tipo de Exame", 
      "Módulo de Exame", "Instituto", "Data de Criação", "Data de Requisição", 
      "Número do Exame", "ID da Série - Orthanc", "UID da Instância - Orthanc", 
      "ID do Estudo - Orthanc", "Reports", "ID do Report", "TLV", "TLW", 
      "Pulmonary Involvement Extent TLV", "Pulmonary Involvement Severity TLW",
      "Low ML", "Low G", "High ML", "High G", "Probabilidade", "Classificação",
      "Observações", "Anotação Enviada", "Edição Enviada", "Médico Solicitante"];

    // Buscar todos os exames da instituição
    const response = await Api.getExams({
      institution: institution?.id,
      limit: 10000,
    });
    
    let allExams = [];

    if (response?.data?.result?.length) {
      allExams = corrigeModulos(response?.data?.result);
    }

    if (!allExams.length) return;

    const sortedExams = [...allExams].sort((a, b) => {
      const idA = Number(a.id) || 0;
      const idB = Number(b.id) || 0;
      return idA - idB;
    });

    const rows = sortedExams.map(exam => [
      exam.id || "",
      exam.patient?.name || "",
      exam.status == 'NEW' ? 'Novo' : exam.status == 'APP' ? 'Aprovado' : exam.status == 'REV' ? 'Revisado' : exam.status == 'INC' ? 'Inconclusivo' : exam.status == 'ERR' ? 'Exame Não Processado' : "Outro",
      exam.type == 'CT' ? 'Tomografia Computorizada' : exam.type == 'CR' ? 'Radiografia Computorizada' : "Outro",
      exam.module|| "",
      exam.institution?.name || "",
      exam.date_created || "",
      exam.date_requested || "",
      exam.registry || "",
      exam.series_id || "",
      exam.series_instance_uid || "",
      exam.study_id || "",
      exam.reports?.length || 0,
      exam.report?.id || "", 
      exam.report?.tlv || "",
      exam.report?.tlw || "",
      exam.report?.pulmonary_involvement_extent_tlv || "",
      exam.report?.pulmonary_involvement_severity_tlw || "",
      exam.report?.low_ml || "",
      exam.report?.low_g || "",
      exam.report?.high_ml || "",
      exam.report?.high_g || "",
      exam.report?.probability || "",
      exam.report?.classification || "",
      exam.report?.feedback_comments || "",
      exam.report?.annotation_mask_sent_to_orthanc ? 'Sim' : 'Não',
      exam.report?.edition_mask_sent_to_orthanc ? 'Sim' : 'Não',
      exam.doctor?.name || ""
    ]);

    const csvContent = [
      headers.join(","), 
      ...rows.map(r => r.map(value => `"${value}"`).join(","))
    ].join("\n");

    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    const dateStr = `${dd}-${mm}-${yyyy}`;
    const fileName = `exames-${dateStr}.csv`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}; */

  return (
    <PageWithMenu showMenu={showMenu} menuStyle={pageWithMenuStyle} examSummary={examSummary} exportExamsToCSV={exportExamsToCSV}>
      <ExamsArchiveStyle>
        <Header
          handleShowMenu={
            breakpoint.width < breakpoints.ipadLandscape.width
              ? handleShowMenu
              : null
          }
        />
        {examSummary ? (
          <>
            <div className="exams-archive-sort-filter-form">
              <div className="sort-filter-field-wrapper">
                <InputField
                  shape="circle"
                  type="text"
                  inputName="search-wxam"
                  value={searchExams}
                  icon={<SearchIcon />}
                  placeholder={translate(
                    'searchExamByMedicalRecordPacient',
                    language
                  )}
                  onChange={(value) => {
                    setExamsPage(0);
                    setSearchExams(value);
                  }}
                  onKeyDown={(key) => {
                    if (key === 'Enter') {
                      setApplyFilter(true);
                    }
                  }}
                  noMarginBottom
                />
              </div>
              <div className="sort-filter-field-wrapper">
                <SelectOptions
                  theme="grayish-blue-outline"
                  shape="circle"
                  isSearchable={true}
                  options={[
                    {
                      key: 0,
                      value: '',
                      text: translate('sort', language),
                    },
                    {
                      key: 1,
                      value: '-date_requested',
                      text: translate('newer', language),
                    },
                    {
                      key: 2,
                      value: 'date_requested',
                      text: translate('older', language),
                    },
                    {
                      key: 3,
                      value: 'registry',
                      text: translate('medicalRecord', language),
                    },
                    {
                      key: 4,
                      value: 'status',
                      text: 'Status',
                    },
                    {
                      key: 5,
                      value: 'type',
                      text: translate('type', language),
                    },
                    {
                      key: 6,
                      value: 'registry',
                      text: translate('medicalRecord', language),
                    },
                    {
                      key: 7,
                      value: '-date_created',
                      text: translate('registration_newer', language),
                    },
                    {
                      key: 8,
                      value: 'date_created',
                      text: translate('registration_older', language),
                    },
                    {
                      key: 9,
                      value: 'patient__name',
                      text: translate('patient_name_asc', language),
                    },
                                        {
                      key: 10,
                      value: '-patient__name',
                      text: translate('patient_name_desc', language),
                    },
                  ]}
                  value={examOrderType}
                  onChange={setExamOrderType}
                />
              </div>
              <div className="sort-filter-field-wrapper">
                <SelectOptions
                  theme="grayish-blue-outline"
                  shape="circle"
                  isSearchable={true}
                  options={[
                    {
                      key: 0,
                      value: 0,
                      text: translate('filterExamByStatus', language),
                    },
                    ...getOptionList(EXAM_STATUS),
                  ]}
                  value={filterByStatus}
                  onChange={(value) =>
                    handleChangeSelect(
                      value,
                      [
                        {
                          key: 0,
                          value: 0,
                          text: translate('filterExamByStatus', language),
                        },
                        ...getOptionList(EXAM_STATUS),
                      ],
                      setFilterByStatus
                    )
                  }
                />
              </div>
              <div className="sort-filter-field-wrapper">
                <SelectOptions
                  theme="grayish-blue-outline"
                  shape="circle"
                  isSearchable={true}
                  options={[
                    {
                      key: 0,
                      value: 0,
                      text: translate('filterByExamType', language),
                    },
                    ...getOptionList(EXAM_TYPES),
                  ]}
                  value={filterByExamType}
                  onChange={(value) =>
                    handleChangeSelect(
                      value,
                      [
                        {
                          key: 0,
                          value: 0,
                          text: translate('filterByExamType', language),
                        },
                        ...getOptionList(EXAM_TYPES),
                      ],
                      setFilterByExamType
                    )
                  }
                />
              </div>
              {doctorsOptions?.length ? (
                <div className="sort-filter-field-wrapper">
                  <SelectOptions
                    theme="grayish-blue-outline"
                    shape="circle"
                    isSearchable={true}
                    options={[
                      {
                        key: 0,
                        value: 0,
                        text: translate('filterExamsByDoctor', language),
                      },
                      ...doctorsOptions,
                    ]}
                    value={filterByDoctor}
                    onChange={(value) =>
                      handleChangeSelect(
                        value,
                        [
                          {
                            key: 0,
                            value: 0,
                            text: translate('filterExamsByDoctor', language),
                          },
                          ...doctorsOptions,
                        ],
                        setFilterByDoctor
                      )
                    }
                  />
                </div>
              ) : null}
              {patientsOptions?.length ? (
                <div className="sort-filter-field-wrapper">
                  <SelectOptions
                    theme="grayish-blue-outline"
                    shape="circle"
                    isSearchable={true}
                    options={[
                      {
                        key: 0,
                        value: 0,
                        text: translate('filterExamsByPatient', language),
                      },
                      ...patientsOptions,
                    ]}
                    value={filterByPatient}
                    onChange={(value) =>
                      handleChangeSelect(
                        value,
                        [
                          {
                            key: 0,
                            value: 0,
                            text: translate('filterExamsByPatient', language),
                          },
                          ...patientsOptions,
                        ],
                        setFilterByPatient
                      )
                    }
                  />
                </div>
              ) : null}
              <div className="approve-results-button">
                <ActionButton
                  theme="default"
                  icon={null}
                  text={translate('resetFilters', language)}
                  transition
                  disabled={false}
                  onClick={handleResetFilters}
                />
              </div>
            </div>
          </>
        ) : (
          <LoadingSection message={translate('loadingExamSummaryAndFilters', language)} />
        )}
        {renderExamReportList}
        {loading ? <Loading /> : null}
      </ExamsArchiveStyle>
    </PageWithMenu>
  );
};

export default ExamsArchive;
